// Scanner PWA route — production-ready voor 300 kaarten.
//
// Features:
// - Tik-om-te-starten: camera start op user-gesture (iOS-vereiste, ook in home-screen PWA)
// - Harde camera-foutafhandeling (permissie/geen camera/in gebruik) met NL-meldingen + retry
// - BarcodeDetector (native, snel) waar ondersteund + fallback-constraints voor oudere iOS
// - Atomic check-in via qr-validate edge function
// - Ambassadeur-herkenning: toont AMBASSADEUR + naam + speldje-status, afvinken bij de deur
// - Manual ticket_no entry als fallback (toetsenbord-icoon, modal)
// - Resilience: errors blokkeren niet, scanner blijft door-runnen
// - Visuele feedback: hoekjes-overlay, kleuren, sound, vibratie

import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Html5Qrcode, Html5QrcodeScannerState, Html5QrcodeSupportedFormats } from "html5-qrcode";
import dayjs from "dayjs";
import { validateQr, checkInByTicketNo, resignTickets, markPinDelivered, loadDoorList, type ScanAmbassador, type DoorListRow } from "@/lib/api";
import { fmtDateTime } from "@/lib/formatters";
import { useTicketsRealtime } from "@/lib/use-tickets-realtime";

type ScanResult =
  | { kind: "ok"; name: string; type: string; ticket_no: string; ambassador: ScanAmbassador | null; ts: number }
  | { kind: "dup"; name: string; type: string; scanned_at: string; ticket_no: string; ambassador: ScanAmbassador | null; ts: number }
  | { kind: "bad"; reason: string; ts: number };

const DEBOUNCE_MS = 700;
const RESULT_HOLD_MS = 2500;
// Zichtbare build-marker — controleer op het startscherm of de PWA de nieuwe versie draait.
const SCANNER_VERSION = "v6 · 18-06 (live sync)";

const isStandalone = (): boolean => {
  try {
    return (
      window.matchMedia?.("(display-mode: standalone)").matches === true ||
      (navigator as unknown as { standalone?: boolean }).standalone === true
    );
  } catch {
    return false;
  }
};

function cameraErrorMessage(err: unknown): string {
  const name = err instanceof Error ? err.name : "";
  const msg = err instanceof Error ? err.message : String(err);
  if (name === "NotAllowedError" || name === "PermissionDeniedError" || /permission/i.test(msg)) {
    return isStandalone()
      ? "Cameratoegang geweigerd. Open Instellingen → Apps → deze app → Camera en zet 'm aan. Lukt dat niet? Open de admin in Safari i.p.v. vanaf je beginscherm."
      : "Cameratoegang geweigerd. Tik op 'aA' in de adresbalk → Website-instellingen → Camera → Toestaan, en probeer opnieuw.";
  }
  if (name === "NotFoundError" || name === "DevicesNotFoundError" || name === "OverconstrainedError") {
    return "Geen geschikte camera gevonden. Probeer opnieuw of gebruik handmatige invoer.";
  }
  if (name === "NotReadableError" || name === "TrackStartError") {
    return "Camera is in gebruik door een andere app. Sluit andere camera-apps en probeer opnieuw.";
  }
  return `Camera-fout: ${msg}`;
}

async function stopScanner(reader: Html5Qrcode | null) {
  if (!reader) return;
  try {
    const state = reader.getState();
    if (state === Html5QrcodeScannerState.SCANNING || state === Html5QrcodeScannerState.PAUSED) {
      await reader.stop();
    }
  } catch (err) {
    console.warn("scanner stop failed", err);
  }
  try {
    reader.clear();
  } catch (err) {
    console.warn("scanner clear failed", err);
  }
}

function deviceId(): string {
  let id = localStorage.getItem("hp-scanner-device");
  if (!id) {
    id = `dev-${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem("hp-scanner-device", id);
  }
  return id;
}

export function Scan() {
  const navigate = useNavigate();
  const [started, setStarted] = useState(false);
  const [starting, setStarting] = useState(false);
  const [current, setCurrent] = useState<ScanResult | null>(null);
  const [history, setHistory] = useState<ScanResult[]>([]);
  const [stats, setStats] = useState({ ok: 0, dup: 0, bad: 0 });
  const [error, setError] = useState<string | null>(null);
  const [camError, setCamError] = useState<string | null>(null);
  const [torchOn, setTorchOn] = useState(false);
  const [torchSupported, setTorchSupported] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [manualValue, setManualValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [closing, setClosing] = useState(false);
  const [pinBusy, setPinBusy] = useState(false);

  const lastTextRef = useRef<string>("");
  const lockRef = useRef<boolean>(false);
  const holdRef = useRef<boolean>(false); // pauzeer scannen terwijl een ambassadeur-banner open staat
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const releasedRef = useRef(false);
  const audioCtxRef = useRef<AudioContext | null>(null);

  function unlockAudio() {
    try {
      const Ctor = (window.AudioContext ||
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext);
      if (!Ctor) return;
      if (!audioCtxRef.current) audioCtxRef.current = new Ctor();
      void audioCtxRef.current.resume();
    } catch {
      /* iOS kan dit blokkeren buiten een gesture — negeer */
    }
  }

  // silent (naam-incheck): wél tellen/piepen/in historie, maar GEEN volledige overlay —
  // tenzij het de ambassadeur zelf is die nog een speldje moet krijgen (dan altijd de banner).
  function pushResult(r: ScanResult, opts?: { silent?: boolean }) {
    setHistory((h) => [r, ...h].slice(0, 5));
    if (r.kind === "ok") setStats((s) => ({ ...s, ok: s.ok + 1 }));
    else if (r.kind === "dup") setStats((s) => ({ ...s, dup: s.dup + 1 }));
    else setStats((s) => ({ ...s, bad: s.bad + 1 }));

    const amb = r.kind !== "bad" ? r.ambassador : null;
    // Houd vast bij ELKE scan van de ambassadeur zélf (niet bij meegenomen gasten — anders
    // blokkeert de rij). Zo handelt de deur bewust het speldje af, én ziet 'ie bij een herhaalde
    // scan duidelijk "AL GESCAND" + of het speldje al is uitgereikt.
    const holdAmb = !!amb && amb.is_self;

    if (amb && amb.is_self) {
      beep("amb");
      vibrate([60, 40, 60, 40, 140]);
    } else {
      beep(r.kind);
      vibrate(r.kind === "ok" ? [60] : r.kind === "dup" ? [40, 40, 40] : [200]);
    }

    // Stille incheck zonder speldje-actie → geen overlay, niets vasthouden.
    if (opts?.silent && !holdAmb) return;

    setCurrent(r);
    if (holdAmb) {
      // Banner blijft staan tot de deur 'm afhandelt (speldje geven of "Verder").
      holdRef.current = true;
    } else {
      // Auto-hide; scanner blijft door-runnen.
      setTimeout(() => {
        setCurrent((cur) => (cur && cur.ts === r.ts ? null : cur));
      }, RESULT_HOLD_MS);
    }
  }

  async function handleScan(text: string) {
    if (lockRef.current || holdRef.current) return;
    if (text === lastTextRef.current) return;
    lockRef.current = true;
    lastTextRef.current = text;

    let allowRetry = false; // bij een fout mag dezelfde QR opnieuw geprobeerd worden
    try {
      const res = await validateQr(text, deviceId());
      if (res.result === "ok") {
        const a = res.attendee ?? {};
        pushResult({
          kind: "ok",
          name: `${a.first_name ?? ""} ${a.last_name ?? ""}`.trim() || "Gast",
          type: res.ticket_type.name,
          ticket_no: res.ticket.ticket_no,
          ambassador: res.ambassador,
          ts: Date.now(),
        });
      } else if (res.result === "already_scanned") {
        const a = res.attendee ?? {};
        pushResult({
          kind: "dup",
          name: `${a.first_name ?? ""} ${a.last_name ?? ""}`.trim() || "Gast",
          type: res.ticket_type.name,
          scanned_at: res.scanned_at,
          ticket_no: res.ticket.ticket_no,
          ambassador: res.ambassador,
          ts: Date.now(),
        });
      } else {
        allowRetry = true; // ongeldig/netwerkfout → opnieuw kunnen scannen
        pushResult({ kind: "bad", reason: res.reason ?? "ongeldig", ts: Date.now() });
      }
    } catch (err) {
      allowRetry = true;
      pushResult({
        kind: "bad",
        reason: err instanceof Error ? err.message : String(err),
        ts: Date.now(),
      });
    }

    // Lock vrijgeven. Bij een geslaagde scan (ok/dup) houden we lastTextRef vast, zodat
    // dezelfde QR die in beeld blijft niet elke 700ms opnieuw wordt gevalideerd (geen spam).
    // Bij een fout wissen we hem zodat herhalen meteen mag.
    setTimeout(() => {
      lockRef.current = false;
      if (allowRetry) lastTextRef.current = "";
    }, DEBOUNCE_MS);
  }

  // Camera-start gebeurt expliciet op user-gesture (iOS laat getUserMedia anders niet toe,
  // vooral niet in een home-screen PWA). We proberen meerdere constraints voor maximale compatibiliteit.
  async function startScanner() {
    if (starting) return;
    setStarting(true);
    setCamError(null);

    if (!window.isSecureContext) {
      setCamError("Onveilige verbinding — camera werkt alleen via https. Open de juiste admin-URL.");
      setStarting(false);
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      setCamError(
        isStandalone()
          ? "Deze weergave geeft geen camera-toegang. Open de admin in Safari i.p.v. vanaf je beginscherm."
          : "Camera niet beschikbaar in deze browser. Gebruik Safari of Chrome, of de handmatige invoer.",
      );
      setStarting(false);
      return;
    }

    await stopScanner(scannerRef.current);
    const reader = new Html5Qrcode("qr-reader", {
      verbose: false,
      formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE],
      useBarCodeDetectorIfSupported: true,
    } as ConstructorParameters<typeof Html5Qrcode>[1]);
    releasedRef.current = false;
    scannerRef.current = reader;

    const qrbox = (vw: number, vh: number) => {
      const min = Math.min(vw, vh);
      // Ruim scanvak zodat de QR makkelijker binnen het kader valt (betere pickup).
      const sz = Math.max(240, Math.min(500, Math.floor(min * 0.82)));
      return { width: sz, height: sz };
    };

    // Constraint-kandidaten, van ideaal → meest tolerant.
    // BELANGRIJK voor leesbaarheid: hoge resolutie + continue scherpstelling. Een QR met een
    // JWT erin is vrij dicht; op lage resolutie of zonder autofocus is die slecht te lezen.
    const attempts: { camera: MediaTrackConstraints; config: Record<string, unknown> }[] = [
      {
        camera: { facingMode: { ideal: "environment" } },
        config: {
          fps: 20,
          qrbox,
          disableFlip: true,
          videoConstraints: {
            facingMode: { ideal: "environment" },
            width: { ideal: 1920 },
            height: { ideal: 1080 },
            focusMode: "continuous",
            advanced: [{ focusMode: "continuous" }],
          },
        },
      },
      {
        // Fallback: lagere resolutie, mocht 1080p geweigerd worden.
        camera: { facingMode: { ideal: "environment" } },
        config: {
          fps: 15,
          qrbox,
          disableFlip: true,
          videoConstraints: { facingMode: { ideal: "environment" }, width: { ideal: 1280 }, height: { ideal: 720 } },
        },
      },
      {
        camera: { facingMode: "environment" } as MediaTrackConstraints,
        config: { fps: 10, qrbox, disableFlip: true },
      },
    ];

    let lastErr: unknown = null;
    let ok = false;
    for (const attempt of attempts) {
      try {
        await reader.start(
          attempt.camera,
          attempt.config as unknown as Parameters<Html5Qrcode["start"]>[1],
          handleScan,
          () => { /* ignore decode errors */ },
        );
        ok = true;
        break;
      } catch (err) {
        lastErr = err;
        // Bij een permissie-fout heeft een andere constraint geen zin.
        if (err instanceof Error && (err.name === "NotAllowedError" || err.name === "PermissionDeniedError")) break;
      }
    }

    // Laatste redmiddel: expliciet de achtercamera via device-id (helpt als facingMode geweigerd wordt).
    if (!ok && !(lastErr instanceof Error && lastErr.name === "NotAllowedError")) {
      try {
        const cams = await Html5Qrcode.getCameras();
        // Kies alleen een herkenbare achtercamera; bij lege/ambigue labels (iOS) niet
        // gokken op de laatste — dat pakt vaak de frontcamera. Dan liever een nette fout.
        const back = cams.find((c) => /back|rear|environment|achter/i.test(c.label));
        const pick = back ?? (cams.length === 1 ? cams[0] : null);
        if (pick) {
          await reader.start(pick.id, { fps: 12, qrbox, disableFlip: true }, handleScan, () => {});
          ok = true;
        }
      } catch (err) {
        lastErr = err;
      }
    }

    if (!ok) {
      await stopScanner(reader);
      scannerRef.current = null;
      setCamError(cameraErrorMessage(lastErr));
      setStarting(false);
      return;
    }

    try {
      const caps = (reader as unknown as { getRunningTrackCapabilities?: () => MediaTrackCapabilities })
        .getRunningTrackCapabilities?.();
      if (caps && "torch" in caps) setTorchSupported(true);
    } catch { /* ignore */ }

    setStarted(true);
    setStarting(false);
  }

  function handleStartTap() {
    unlockAudio();      // moet binnen de gesture
    void startScanner();
  }

  useEffect(() => {
    return () => {
      releaseScanner(scannerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function releaseScanner(reader = scannerRef.current) {
    if (!reader || releasedRef.current) return;
    releasedRef.current = true;
    scannerRef.current = null;
    void stopScanner(reader);
  }

  function closeScanner() {
    setClosing(true);
    releaseScanner();
    navigate("/", { replace: true });
  }

  function dismissResult() {
    // Hervat scannen, maar houd lastTextRef vast zodat dezelfde ambassadeur-QR die nog in
    // beeld is niet meteen opnieuw vasthoudt. Een andere code passeert de guard wel.
    holdRef.current = false;
    lockRef.current = false;
    setCurrent(null);
  }

  async function onMarkPin(ambassadorId: string, resultTs: number) {
    if (pinBusy) return;
    setPinBusy(true);
    try {
      await markPinDelivered(ambassadorId);
      const mark = (r: ScanResult): ScanResult =>
        r.kind !== "bad" && r.ambassador?.id === ambassadorId
          ? { ...r, ambassador: { ...r.ambassador, pin_status: "delivered" } }
          : r;
      setCurrent((c) => (c ? mark(c) : c));
      setHistory((h) => h.map(mark));
      vibrate([40, 40]);
      // korte bevestiging tonen, dan automatisch verder (lastTextRef blijft staan → geen re-hold)
      setTimeout(() => {
        setCurrent((cur) => (cur && cur.ts === resultTs ? null : cur));
        holdRef.current = false;
        lockRef.current = false;
      }, 1200);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setTimeout(() => setError(null), 4000);
      // Belangrijk: een mislukte markering mag de scanner niet permanent blokkeren.
      holdRef.current = false;
      lockRef.current = false;
    } finally {
      setPinBusy(false);
    }
  }

  async function toggleTorch() {
    const reader = scannerRef.current;
    if (!reader) return;
    try {
      const next = !torchOn;
      await (reader as unknown as { applyVideoConstraints: (c: MediaTrackConstraints) => Promise<void> })
        .applyVideoConstraints({ advanced: [{ torch: next } as MediaTrackConstraintSet] });
      setTorchOn(next);
    } catch (err) {
      console.warn("torch toggle failed", err);
    }
  }

  // Incheck via ticketnr OF naam-zoek (beide gebruiken dezelfde atomic check-in).
  async function doCheckIn(ticketNo: string) {
    const v = ticketNo.trim();
    if (!v || busy) return;
    setBusy(true);
    try {
      const res = await checkInByTicketNo(v, deviceId());
      if (res.result === "ok") {
        const a = res.attendee ?? {};
        pushResult({
          kind: "ok",
          name: `${a.first_name ?? ""} ${a.last_name ?? ""}`.trim() || "Gast",
          type: res.ticket_type?.name ?? "Ticket",
          ticket_no: res.ticket.ticket_no,
          ambassador: res.ambassador,
          ts: Date.now(),
        });
      } else if (res.result === "already_scanned") {
        const a = res.attendee ?? {};
        pushResult({
          kind: "dup",
          name: `${a.first_name ?? ""} ${a.last_name ?? ""}`.trim() || "Gast",
          type: res.ticket_type?.name ?? "Ticket",
          scanned_at: res.scanned_at,
          ticket_no: res.ticket.ticket_no,
          ambassador: res.ambassador,
          ts: Date.now(),
        });
      } else {
        pushResult({ kind: "bad", reason: res.reason, ts: Date.now() });
      }
      setManualValue("");
      setManualOpen(false);
    } catch (err) {
      pushResult({
        kind: "bad",
        reason: err instanceof Error ? err.message : String(err),
        ts: Date.now(),
      });
    } finally {
      setBusy(false);
    }
  }

  function submitManual() {
    void doCheckIn(manualValue);
  }

  // Naam-incheck: venster blijft open (meerdere achter elkaar). Geen pop-up — behalve als het
  // de ambassadeur zelf is die nog een speldje moet krijgen; dan sluit het venster en komt de banner.
  async function checkInByName(ticketNo: string): Promise<{ ok: boolean; popup: boolean; error: string | null }> {
    const v = ticketNo.trim();
    if (!v) return { ok: false, popup: false, error: null };
    try {
      const res = await checkInByTicketNo(v, deviceId());
      if (res.result === "invalid") return { ok: false, popup: false, error: res.reason };
      const a = res.attendee ?? {};
      const name = `${a.first_name ?? ""} ${a.last_name ?? ""}`.trim() || "Gast";
      const amb = res.ambassador;
      const popup = !!amb && amb.is_self && amb.pin_status !== "delivered";
      const result: ScanResult = res.result === "ok"
        ? { kind: "ok", name, type: res.ticket_type?.name ?? "Ticket", ticket_no: res.ticket.ticket_no, ambassador: amb, ts: Date.now() }
        : { kind: "dup", name, type: res.ticket_type?.name ?? "Ticket", scanned_at: res.scanned_at, ticket_no: res.ticket.ticket_no, ambassador: amb, ts: Date.now() };
      pushResult(result, { silent: !popup });
      if (popup) setManualOpen(false);  // banner tonen → venster dicht
      return { ok: true, popup, error: null };
    } catch (err) {
      return { ok: false, popup: false, error: err instanceof Error ? err.message : String(err) };
    }
  }

  async function runResignAll() {
    if (!confirm("Tickets opnieuw signen? Dit fixt oude QR's na een key-rotatie of bug. Veilig om meerdere keren te draaien.")) return;
    setBusy(true);
    try {
      const r = await resignTickets();
      alert(`✓ ${r.resigned}/${r.total} tickets opnieuw gesigned${r.failed ? ` (${r.failed} mislukt)` : ""}`);
    } catch (err) {
      alert(`Fout: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="scanner-shell">
      <div className="scanner-head">
        <div className="t">◉ Scanner</div>
        <div className="stats">
          <span className="ok">✓ {stats.ok}</span>
          <span className="dup">↺ {stats.dup}</span>
          <span className="bad">✗ {stats.bad}</span>
        </div>
        <div style={{ display: "flex", gap: "0.4rem" }}>
          {torchSupported && started && (
            <button
              type="button"
              className="pill"
              onClick={toggleTorch}
              style={{ background: torchOn ? "var(--c-orange)" : undefined, color: torchOn ? "var(--c-deep)" : undefined }}
              title="Flits aan/uit"
            >💡</button>
          )}
          <button
            type="button"
            className="pill"
            onClick={() => setManualOpen(true)}
            title="Handmatig ticketnr invoeren"
          >⌨</button>
          <button
            type="button"
            className="pill"
            onClick={runResignAll}
            disabled={busy}
            title="Eenmalig: oude tickets opnieuw signen"
          >🔄</button>
          <button
            type="button"
            className="pill scanner-close"
            onClick={closeScanner}
            disabled={closing}
            title="Terug naar admin"
          >✕</button>
        </div>
      </div>

      <div className="scanner-cam">
        <div id="qr-reader" />
        {started && <div className="scan-frame" aria-hidden />}
        {!started && (
          <StartScreen
            starting={starting}
            error={camError}
            onStart={handleStartTap}
            onManual={() => setManualOpen(true)}
          />
        )}
        {current && (
          <ResultOverlay
            r={current}
            pinBusy={pinBusy}
            onMarkPin={onMarkPin}
            onDismiss={dismissResult}
          />
        )}
      </div>

      <div className="scanner-strip">
        <div className="strip-l">Recent</div>
        {history.length === 0 ? (
          <div className="strip-empty">{started ? "Wacht op QR…" : "Scanner nog niet gestart"}</div>
        ) : (
          history.map((r) => <StripItem key={r.ts} r={r} />)
        )}
      </div>

      {manualOpen && (
        <EntryModal
          value={manualValue}
          onChange={setManualValue}
          onSubmit={submitManual}
          onCheckInName={checkInByName}
          onClose={() => { setManualOpen(false); setManualValue(""); }}
          busy={busy}
        />
      )}

      {error && <div className="toast error">{error}</div>}
    </div>
  );
}

function StartScreen({
  starting, error, onStart, onManual,
}: {
  starting: boolean;
  error: string | null;
  onStart: () => void;
  onManual: () => void;
}) {
  return (
    <div className="scanner-start">
      <button type="button" className="scanner-start-btn" onClick={onStart} disabled={starting}>
        {starting ? "⏳" : "▶"}
      </button>
      <div className="scanner-start-t">{starting ? "Camera openen…" : "Scanner starten"}</div>
      <div className="scanner-start-sub">
        {starting ? "Geef toestemming voor de camera." : "Tik om de camera te openen en QR-codes te scannen."}
      </div>
      {error && <div className="scanner-start-err">⚠ {error}</div>}
      <button type="button" className="btn-admin ghost sm" onClick={onManual} style={{ marginTop: "1.1rem" }}>
        ⌨ Handmatig ticketnr invoeren
      </button>
      <div className="scanner-start-ver">Scanner {SCANNER_VERSION}</div>
    </div>
  );
}

function AmbassadorBadge({
  amb, pinBusy, onMarkPin, resultTs,
}: {
  amb: ScanAmbassador;
  pinBusy: boolean;
  onMarkPin: (id: string, ts: number) => void;
  resultTs: number;
}) {
  const delivered = amb.pin_status === "delivered";
  return (
    <div className={`amb-badge ${delivered || !amb.is_self ? "done" : ""}`}>
      <div className="amb-h">🎖️ AMBASSADEUR{amb.is_self ? "" : " · groep"}</div>
      <div className="amb-name">{amb.name || "—"}</div>
      {!amb.is_self ? (
        <div className="amb-pin done">Gast uit de groep van deze ambassadeur</div>
      ) : delivered ? (
        <div className="amb-pin done">✓ Speldje al uitgereikt</div>
      ) : (
        <>
          <div className="amb-pin">📍 Geef deze ambassadeur een speldje</div>
          <button
            type="button"
            className="amb-pin-btn"
            disabled={pinBusy}
            onClick={(e) => { e.stopPropagation(); onMarkPin(amb.id, resultTs); }}
          >
            {pinBusy ? "Bezig…" : "Speldje uitgereikt ✓"}
          </button>
        </>
      )}
    </div>
  );
}

function ResultOverlay({
  r, pinBusy, onMarkPin, onDismiss,
}: {
  r: ScanResult;
  pinBusy: boolean;
  onMarkPin: (id: string, ts: number) => void;
  onDismiss: () => void;
}) {
  const cls = r.kind === "ok" ? "ok" : r.kind === "dup" ? "dup" : "bad";
  const amb = r.kind !== "bad" ? r.ambassador : null;
  // Elke scan van de ambassadeur zélf blijft staan tot de deur 'm afhandelt — ook een
  // herhaalde scan, zodat duidelijk "AL GESCAND" + de speldje-status zichtbaar blijft.
  const held = !!amb && amb.is_self;
  return (
    <div className={`scan-overlay ${cls} ${held ? "has-amb" : ""}`}>
      {r.kind === "ok" && (
        <>
          <div className="big">✓</div>
          <div className="t">WELKOM</div>
          <div className="name">{r.name}</div>
          <div className="meta">{r.type} · {r.ticket_no}</div>
        </>
      )}
      {r.kind === "dup" && (
        <>
          <div className="big">↺</div>
          <div className="t">AL GESCAND</div>
          <div className="name">{r.name}</div>
          <div className="meta">{fmtDateTime(r.scanned_at)} ({dayjs(r.scanned_at).fromNow()})</div>
        </>
      )}
      {r.kind === "bad" && (
        <>
          <div className="big">✗</div>
          <div className="t">ONGELDIG</div>
          <div className="meta">{r.reason}</div>
        </>
      )}

      {amb && <AmbassadorBadge amb={amb} pinBusy={pinBusy} onMarkPin={onMarkPin} resultTs={r.ts} />}

      {held && (
        <button type="button" className="amb-next" onClick={onDismiss}>
          Verder scannen ▶
        </button>
      )}
    </div>
  );
}

function StripItem({ r }: { r: ScanResult }) {
  const cls = r.kind === "ok" ? "ok" : r.kind === "dup" ? "dup" : "bad";
  const amb = r.kind !== "bad" ? r.ambassador : null;
  const label = r.kind === "ok" ? `✓ ${r.name}` : r.kind === "dup" ? `↺ ${r.name}` : `✗ ${r.reason}`;
  return (
    <div className={`strip-item ${cls}`}>
      <div className="lbl">{amb ? "🎖️ " : ""}{label}</div>
      <div className="time">{dayjs(r.ts).format("HH:mm:ss")}</div>
    </div>
  );
}

function EntryModal({
  value, onChange, onSubmit, onCheckInName, onClose, busy,
}: {
  value: string;
  onChange: (v: string) => void;
  onSubmit: () => void;
  onCheckInName: (ticketNo: string) => Promise<{ ok: boolean; popup: boolean; error: string | null }>;
  onClose: () => void;
  busy: boolean;
}) {
  const [mode, setMode] = useState<"name" | "ticket">("name");
  const [nameQ, setNameQ] = useState("");
  const [list, setList] = useState<DoorListRow[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [checkingId, setCheckingId] = useState<string | null>(null);
  const [rowErr, setRowErr] = useState<string | null>(null);

  useEffect(() => {
    if (mode !== "name" || list !== null || loading) return;
    setLoading(true);
    loadDoorList()
      .then((rows) => { setList(rows); setLoadErr(null); })
      .catch((e) => setLoadErr(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [mode, list, loading]);

  // Realtime: check-ins van ANDERE apparaten meteen in de open naam-lijst tonen.
  useTicketsRealtime((n) => {
    setList((prev) => (prev === null ? prev : prev.map((x) =>
      x.ticket_id === n.id ? { ...x, status: n.status, checked_in_at: n.checked_in_at } : x)));
  });

  // Incheck vanuit de naam-lijst: venster blijft open, regel springt op "✓ binnen".
  async function checkInRow(row: DoorListRow) {
    if (checkingId) return;
    setRowErr(null);
    setCheckingId(row.ticket_id);
    const res = await onCheckInName(row.ticket_no);
    setCheckingId(null);
    if (res.error) { setRowErr(`${row.name}: ${res.error}`); return; }
    if (res.popup) return; // ambassadeur-speldje → venster sluit, banner verschijnt
    if (res.ok) {
      setList((prev) => (prev ?? []).map((x) =>
        x.ticket_id === row.ticket_id ? { ...x, status: "scanned", checked_in_at: new Date().toISOString() } : x));
    }
  }

  const q = nameQ.trim().toLowerCase();
  const matches = q.length >= 2
    ? (list ?? [])
        .filter((r) => r.name.toLowerCase().includes(q))
        .sort((a, b) => a.name.localeCompare(b.name, "nl"))
        .slice(0, 40)
    : [];

  return (
    <div className="manual-backdrop" onClick={onClose}>
      <div className="manual-panel" onClick={(e) => e.stopPropagation()}>
        <div className="tabs" style={{ marginBottom: "0.9rem" }}>
          <button type="button" className={`tab ${mode === "name" ? "active" : ""}`} onClick={() => setMode("name")}>🔎 Op naam</button>
          <button type="button" className={`tab ${mode === "ticket" ? "active" : ""}`} onClick={() => setMode("ticket")}>⌨ Ticketnr</button>
        </div>

        {mode === "ticket" ? (
          <>
            <div style={{ fontSize: "0.78rem", opacity: 0.7, marginBottom: "0.8rem" }}>
              Typ het complete ticketnr (bv. <code>HP26-T-A1B2C3D4-1</code>).
            </div>
            <input
              autoFocus
              value={value}
              onChange={(e) => onChange(e.target.value)}
              placeholder="HP26-T-..."
              onKeyDown={(e) => { if (e.key === "Enter") onSubmit(); if (e.key === "Escape") onClose(); }}
              style={{
                width: "100%", padding: "0.8rem", fontSize: "1rem",
                fontFamily: "var(--f-mono)", textTransform: "uppercase",
                background: "var(--c-deep)", color: "var(--c-cream)",
                border: "1px solid var(--c-orange)", borderRadius: 4,
              }}
            />
            <div style={{ display: "flex", gap: "0.6rem", marginTop: "1rem", justifyContent: "flex-end" }}>
              <button type="button" className="btn-admin ghost sm" onClick={onClose}>Sluit</button>
              <button type="button" className="btn-admin sm" onClick={onSubmit} disabled={busy || !value.trim()}>
                {busy ? "Bezig…" : "→ Check in"}
              </button>
            </div>
          </>
        ) : (
          <>
            <input
              autoFocus
              value={nameQ}
              onChange={(e) => setNameQ(e.target.value)}
              placeholder="Zoek op naam…"
              onKeyDown={(e) => { if (e.key === "Escape") onClose(); }}
              style={{
                width: "100%", padding: "0.8rem", fontSize: "1rem",
                background: "var(--c-deep)", color: "var(--c-cream)",
                border: "1px solid var(--c-orange)", borderRadius: 4,
              }}
            />
            {rowErr && <div className="door-hint err" style={{ textAlign: "left" }}>⚠ {rowErr}</div>}
            <div className="door-results">
              {loading && <div className="door-hint"><span className="spinner" /> Laden…</div>}
              {loadErr && <div className="door-hint err">⚠ {loadErr}</div>}
              {!loading && !loadErr && q.length < 2 && <div className="door-hint">Typ minstens 2 letters van de naam.</div>}
              {!loading && !loadErr && q.length >= 2 && matches.length === 0 && <div className="door-hint">Geen gast gevonden voor “{nameQ}”.</div>}
              {matches.map((r) => {
                const inside = r.status === "scanned";
                const blocked = r.status === "void";
                const checking = checkingId === r.ticket_id;
                return (
                  <div key={r.ticket_id} className="door-row">
                    <div className="door-info">
                      <div className="door-name">{r.ambassador_name ? "🎖️ " : ""}{r.name}</div>
                      <div className="door-sub">
                        {r.ticket_type}{r.ambassador_name ? ` · ambassadeur ${r.ambassador_name}` : ""}
                      </div>
                    </div>
                    {blocked ? (
                      <span className="door-st void">geblokkeerd</span>
                    ) : inside ? (
                      <span className="door-st in">✓ binnen{r.checked_in_at ? ` ${dayjs(r.checked_in_at).format("HH:mm")}` : ""}</span>
                    ) : (
                      <button type="button" className="btn-admin sm" disabled={checking || checkingId !== null} onClick={() => checkInRow(r)}>
                        {checking ? "…" : "Inchecken"}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "0.8rem" }}>
              <button type="button" className="btn-admin ghost sm" onClick={onClose}>Sluit</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function beep(kind: "ok" | "dup" | "bad" | "amb") {
  try {
    const Ctor = (window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext);
    if (!Ctor) return;
    const ctx = new Ctor();
    const playTone = (freq: number, start: number, dur: number) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.frequency.value = freq;
      gain.gain.value = 0.05;
      osc.start(ctx.currentTime + start);
      osc.stop(ctx.currentTime + start + dur);
    };
    if (kind === "amb") {
      // vrolijke twee-toon voor ambassadeur
      playTone(880, 0, 0.12);
      playTone(1320, 0.13, 0.16);
      setTimeout(() => ctx.close(), 400);
    } else {
      const freq = kind === "ok" ? 880 : kind === "dup" ? 660 : 220;
      playTone(freq, 0, kind === "bad" ? 0.25 : 0.12);
      setTimeout(() => ctx.close(), kind === "bad" ? 300 : 180);
    }
  } catch { /* iOS Safari needs user gesture */ }
}

function vibrate(pattern: number[]) {
  try {
    if ("vibrate" in navigator) navigator.vibrate(pattern);
  } catch { /* ignore */ }
}
